import { inject, Injectable, InjectionToken, NgZone } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, Router } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Browser, BrowserPlugin } from '@capacitor/browser';
import { isWebOnlyPath } from '@cleverly/shared/mobile-device/web-only/web-only-paths';
import { filter, firstValueFrom, timeout } from 'rxjs';

/** Upper bound for waiting on the initial navigation before a web-only link is handed to the browser. */
export const FIRST_NAVIGATION_TIMEOUT_MS = 10_000;

/** The Capacitor Browser plugin, injectable so tests can replace it (plugin proxies cannot be spied on). */
export const CAPACITOR_BROWSER = new InjectionToken<Pick<BrowserPlugin, 'open'>>('CAPACITOR_BROWSER', {
    providedIn: 'root',
    factory: () => Browser,
});

/**
 * Deep Link Service
 * Handles incoming app URL deep links (Universal Links / App Links) and routes them appropriately.
 * Web-only paths (checkout) are handed to the browser and never rendered inside the app.
 */
@Injectable({
    providedIn: 'root'
})
export class DeepLinkService {
    private router = inject(Router);
    private zone = inject(NgZone);
    private browser = inject(CAPACITOR_BROWSER);

    /**
     * Initialize deep link handling
     * Sets up listener for app URL open events
     */
    public initialize(): void {
        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            this.zone.run(() => this.navigateToDeepLink(event.url));
        });
    }

    /**
     * Route a deep link: web-only paths go to the browser, everything else is navigated in-app.
     *
     * @param url - The full URL that opened the app
     */
    public navigateToDeepLink(url: string): void {
        let parsed: URL;
        try {
            parsed = new URL(url);
        } catch {
            console.warn('[DeepLink] Ignoring unparsable URL', url);
            return;
        }

        const slug = parsed.pathname + parsed.search + parsed.hash;

        if (isWebOnlyPath(parsed.pathname)) {
            void this.openWebOnly(url, slug);
            return;
        }

        this.router.navigateByUrl(slug);
    }

    /**
     * Web-only links: show the in-app "not available in the app" page first, then open the link in the
     * in-app browser on top of it. The in-app page is the safety net: `Browser.open` resolves as soon as
     * the native side has *asked* to present the browser (on iOS the presentation is fire-and-forget and
     * only rejects for unparsable URLs), so a presentation that silently fails during cold start would
     * otherwise leave the user on the home screen with nothing to see.
     *
     * Uses the in-app browser (SFSafariViewController / Custom Tab) on purpose: opening the system
     * browser with an app.cleverly.de URL can loop straight back into the app while the OS still
     * associates that path with it (cached AASA, Android < 15).
     */
    private async openWebOnly(url: string, slug: string): Promise<void> {
        await this.waitForFirstNavigation();
        await this.zone.run(() => this.router.navigateByUrl(slug));
        try {
            await this.browser.open({ url });
        } catch (error) {
            console.error('[DeepLink] Opening web-only link in browser failed', url, error);
        }
    }

    /**
     * Resolves once bootstrap, splash and the initial routing are done, whatever the outcome of that
     * navigation (ended, cancelled by a guard, errored, skipped). Bounded by a timeout so a link is
     * never dropped silently.
     */
    private async waitForFirstNavigation(): Promise<void> {
        if (this.router.navigated) {
            return;
        }

        try {
            await firstValueFrom(
                this.router.events.pipe(
                    filter(
                        (e) =>
                            e instanceof NavigationEnd ||
                            e instanceof NavigationCancel ||
                            e instanceof NavigationError ||
                            e instanceof NavigationSkipped
                    ),
                    timeout(FIRST_NAVIGATION_TIMEOUT_MS)
                )
            );
        } catch {
            console.warn('[DeepLink] Initial navigation did not settle in time, continuing anyway');
        }
    }
}
